import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { anotarVisita } from '@/lib/visitante';
import { mascaraCPF, mascaraTelefone, validarCPF } from '@/pages/inscricao/utils';
import { chaveCarteiraLocal, carteiraParaUsar } from '@/pages/loja/utils';
import { validar, irParaOPrimeiro, apontarErro, type CampoGuiado } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarCentavos, parseValorCentavos } from './utils';
import Card from '@/components/ui/Card';
import TopoIngresso from './components/TopoIngresso';
import SeletorQuantidade from './components/SeletorQuantidade';
import ResumoValores from './components/ResumoValores';
import CartaoIdentificacao from './components/CartaoIdentificacao';
import DadosComprador from './components/DadosComprador';
import type {
  ResumoLinha,
  ModalidadeIngresso,
  EventoIngresso,
  CartaoIngresso,
  CompraIngresso,
} from './types';
import { agruparPorModalidade, filtrarLotes, nomeTemLote } from '@/pages/evento/lotes';
import type { Participacao } from '@/types/evento-publico';

// "21/11 · 19:30" — data curta do topo da compra.
function formatarDataCurta(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  const hora = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${dia} · ${hora}`;
}

// Total do pedido, em centavos, a partir das linhas de cobrança do banco.
function totalDoResumo(resumo: ResumoLinha[], quantidade: number): number {
  const total = resumo.find((l) => l.destaque);
  return total ? parseValorCentavos(total.valor) * quantidade : 0;
}

// WhatsApp válido tem 10 ou 11 dígitos (DDD + número), com ou sem máscara.
function telefoneValido(v: string): boolean {
  const d = v.replace(/\D/g, '');
  return d.length === 10 || d.length === 11;
}

export default function IngressoPage() {
  const { modalidadeId } = useParams<{ modalidadeId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const opcaoIdParam = searchParams.get('opcao');
  const opcaoIdNum =
    opcaoIdParam && !Number.isNaN(Number(opcaoIdParam)) ? Number(opcaoIdParam) : null;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [mod, setMod] = useState<ModalidadeIngresso | null>(null);
  const [evento, setEvento] = useState<EventoIngresso | null>(null);
  const [resumo, setResumo] = useState<ResumoLinha[]>([]);
  const [opcaoNome, setOpcaoNome] = useState<string | null>(null);

  // Vendas encerradas (evento passou, lote esgotou ou janela fechou).
  // Guarda o motivo para explicar à pessoa — null = venda aberta.
  const [encerrada, setEncerrada] = useState<string | null>(null);

  const [quantidade, setQuantidade] = useState(1);
  const [cartoes, setCartoes] = useState<CartaoIngresso[]>([]);
  const [errosCartao, setErrosCartao] = useState<
    Array<{ nome?: string; cpf?: string; whatsapp?: string }>
  >([]);
  // Só mostra os erros de todos os campos depois que a pessoa tenta pagar.
  const [forcarErros, setForcarErros] = useState(false);
  // Cada toque no Pagar incompleto soma 1: os campos com erro tremem.
  const [tremor, setTremor] = useState(0);

  // Quem está comprando (só quando a participação não pede dados por ingresso).
  const [logado, setLogado] = useState(false);
  const [temCarteira, setTemCarteira] = useState(false);
  const [nomeCarteira, setNomeCarteira] = useState<string | null>(null);
  const [nomeComprador, setNomeComprador] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [errosComprador, setErrosComprador] = useState<{
    nome?: string;
    whatsapp?: string;
  }>({});

  // Dados da conta logada, para o botão "Sou eu" do 1º ingresso.
  const [perfilLogado, setPerfilLogado] = useState<{
    nome: string;
    cpf: string;
    telefone: string;
  } | null>(null);

  const [criando, setCriando] = useState(false);
  const [bloqueio, setBloqueio] = useState(false);
  const visitaRegistrada = useRef(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro(null);
      setBloqueio(false);
      setEncerrada(null);
      try {
        // Login (opcional — a compra funciona sem conta).
        if (ativo) setLogado(!!user);

        // Dados da conta para o botão "Sou eu" (preenche o 1º ingresso).
        if (user) {
          const { data: perfilData } = await supabase
            .from('vw_meu_perfil')
            .select('nome, cpf, telefone')
            .maybeSingle();
          if (ativo && perfilData) {
            setPerfilLogado(perfilData as { nome: string; cpf: string; telefone: string });
          }
        }

        const { data: modData, error: errMod } = await supabase
          .from('modalidades')
          .select('id, nome, evento_id, exige_identificacao, max_por_compra, valor_centavos, inscricoes_fecham_em')
          .eq('id', Number(modalidadeId))
          .maybeSingle();
        if (errMod) throw errMod;
        if (!modData) {
          if (ativo) setErro('Participação não encontrada.');
          return;
        }
        const m = modData as ModalidadeIngresso;
        if (ativo) {
          setMod(m);
          setQuantidade(1);
          setCartoes(m.exige_identificacao ? [{ nome: '', cpf: '', whatsapp: '' }] : []);
          setErrosCartao([]);
        }

        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('nome, slug, inicio_em, fim_em, local_nome, cidade, uf, imagem_mobile_url, imagem_desktop_url')
          .eq('id', m.evento_id)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!ev) {
          if (ativo) setErro('Participação não encontrada.');
          return;
        }
        const info = ev as EventoIngresso;
        if (ativo) setEvento(info);

        // Vendas já encerradas? Usa só o que esta tela já carrega: o fim do
        // evento, a janela da modalidade e a situação em vw_participacoes.
        let motivoFim: string | null = null;
        const fimDoEvento = new Date(info.fim_em || info.inicio_em || '').getTime();
        if (Number.isFinite(fimDoEvento) && fimDoEvento < Date.now()) {
          motivoFim = 'Este evento já aconteceu.';
        }
        if (!motivoFim && m.inscricoes_fecham_em) {
          const fecha = new Date(m.inscricoes_fecham_em).getTime();
          if (Number.isFinite(fecha) && fecha < Date.now()) {
            motivoFim = 'As vendas deste ingresso já encerraram.';
          }
        }

        // Mesma regra da página do evento: um lote só abre quando o anterior
        // fecha ou esgota. Se este lote ainda estiver escondido, avisamos.
        try {
          const { data: partData } = await supabase
            .from('vw_participacoes')
            .select('*')
            .eq('evento_id', m.evento_id);
          const linhas = (partData as Participacao[]) || [];
          const minhaLinha =
            linhas.find((p) => p.modalidade_id === m.id && p.opcao_id === opcaoIdNum) ||
            linhas.find((p) => p.modalidade_id === m.id);
          if (minhaLinha && !motivoFim) {
            if (minhaLinha.situacao === 'encerradas') {
              motivoFim = 'As vendas deste ingresso já encerraram.';
            } else if (minhaLinha.situacao === 'esgotado' || minhaLinha.restantes === 0) {
              motivoFim = 'Os ingressos deste lote acabaram.';
            } else if (minhaLinha.inscricoes_fecham_em) {
              const fecha = new Date(minhaLinha.inscricoes_fecham_em).getTime();
              if (Number.isFinite(fecha) && fecha < Date.now()) {
                motivoFim = 'As vendas deste ingresso já encerraram.';
              }
            }
          }
          const base = agruparPorModalidade(linhas);
          if (!motivoFim && base.flat().some((p) => nomeTemLote(p.modalidade))) {
            const { grupos } = filtrarLotes(base, true);
            const permitidas = new Set(grupos.flat().map((p) => p.modalidade_id));
            if (ativo) setBloqueio(!permitidas.has(m.id));
          }
        } catch {
          /* sem bloquear: segue o fluxo normal */
        }
        if (ativo) setEncerrada(motivoFim);

        if (opcaoIdNum) {
          const { data: opcaoData } = await supabase
            .from('opcoes')
            .select('nome')
            .eq('id', opcaoIdNum)
            .maybeSingle();
          if (ativo) setOpcaoNome(opcaoData ? (opcaoData as { nome: string }).nome : null);
        }

        // O preço nunca é calculado na tela — vem pronto do banco.
        const { data: resumoData, error: errResumo } = await supabase.rpc('resumo_cobranca', {
          p_modalidade_id: Number(modalidadeId),
          p_opcao_id: opcaoIdNum,
        });
        if (errResumo) throw errResumo;
        if (ativo) setResumo((resumoData as ResumoLinha[]) || []);

        // Carteira já salva neste celular.
        const carteiraSalva = await carteiraParaUsar(m.evento_id);
        if (ativo && carteiraSalva) {
          setTemCarteira(true);
          try {
            const { data: cartData } = await supabase.rpc('loja_carteira_publica', {
              p_carteira: carteiraSalva,
            });
            const nomeCart =
              (cartData as { carteira?: { nome?: string } } | null)?.carteira?.nome ?? null;
            if (ativo && nomeCart) setNomeCarteira(nomeCart);
          } catch {
            /* segue sem o nome */
          }
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [modalidadeId, opcaoIdNum, user]);

  // Anota a visita uma única vez por carregamento, depois que o evento carregou.
  // Fogo e esquece: é só um contador, nunca pode atrapalhar quem quer comprar.
  useEffect(() => {
    if (visitaRegistrada.current) return;
    if (!evento?.slug) return;
    visitaRegistrada.current = true;
    anotarVisita(evento.slug);
  }, [evento]);

  const maxPorCompra = mod?.max_por_compra ?? 1;
  const totalCentavos = useMemo(
    () => totalDoResumo(resumo, quantidade),
    [resumo, quantidade],
  );

  // Só pede nome/WhatsApp (participação sem identificação) quando não há carteira
  // e não está logado.
  const pularDados = temCarteira || logado;

  function mudarQuantidade(nova: number) {
    const q = Math.max(1, Math.min(maxPorCompra, nova));
    setQuantidade(q);
    setForcarErros(false);
    setErrosCartao([]);
    setCartoes((cs) => {
      if (cs.length === q) return cs;
      if (cs.length < q) {
        return [
          ...cs,
          ...Array.from({ length: q - cs.length }, () => ({ nome: '', cpf: '', whatsapp: '' })),
        ];
      }
      return cs.slice(0, q);
    });
  }

  function atualizarCartao(indice: number, patch: Partial<CartaoIngresso>) {
    setCartoes((cs) =>
      cs.map((c, i) => {
        if (i !== indice) return c;
        const novo = { ...c, ...patch };
        if (patch.cpf !== undefined) novo.cpf = mascaraCPF(patch.cpf);
        if (patch.whatsapp !== undefined) novo.whatsapp = mascaraTelefone(patch.whatsapp);
        return novo;
      }),
    );
    setErrosCartao((es) => es.map((e, i) => (i === indice ? {} : e)));
  }

  // "Sou eu": preenche o 1º ingresso com nome/CPF/telefone da conta logada.
  function preencherComigo() {
    if (!perfilLogado) return;
    setCartoes((cs) =>
      cs.map((c, i) =>
        i === 0
          ? {
              nome: perfilLogado.nome,
              cpf: mascaraCPF(perfilLogado.cpf || ''),
              whatsapp: mascaraTelefone(perfilLogado.telefone || ''),
            }
          : c,
      ),
    );
    setErrosCartao((es) => es.map((e, i) => (i === 0 ? {} : e)));
  }

  function mudarNomeComprador(v: string) {
    setNomeComprador(v);
    setErrosComprador((e) => (e ? { ...e, nome: undefined } : undefined));
  }

  function esquecerCarteira() {
    if (mod) localStorage.removeItem(chaveCarteiraLocal(mod.evento_id));
    setTemCarteira(false);
    setNomeCarteira(null);
  }

  function mudarWhatsapp(v: string) {
    setWhatsapp(v);
    setErrosComprador((e) => (e ? { ...e, whatsapp: undefined } : undefined));
  }

  // Validação ao sair do campo (blur) — não bloqueia a digitação.
  function validarNomeNoBlur(indice: number) {
    const c = cartoes[indice];
    if (!c) return;
    if (!c.nome.trim()) {
      setErrosCartao((es) =>
        es.map((e, i) =>
          i === indice ? { ...e, nome: 'Informe o nome de quem vai usar este ingresso.' } : e,
        ),
      );
    }
  }

  function validarCpfNoBlur(indice: number) {
    const c = cartoes[indice];
    if (!c) return;
    const cpfLimpo = c.cpf.replace(/\D/g, '');
    if (cpfLimpo && !validarCPF(c.cpf)) {
      setErrosCartao((es) =>
        es.map((e, i) =>
          i === indice ? { ...e, cpf: 'CPF inválido — confira os 11 números.' } : e,
        ),
      );
    }
  }

  function validarWhatsappCartaoNoBlur(indice: number) {
    const c = cartoes[indice];
    if (!c) return;
    const d = c.whatsapp.replace(/\D/g, '');
    if (d.length > 0 && !telefoneValido(c.whatsapp)) {
      setErrosCartao((es) =>
        es.map((e, i) =>
          i === indice
            ? { ...e, whatsapp: `Confira o WhatsApp com DDD do ingresso ${indice + 1}.` }
            : e,
        ),
      );
    }
  }

  function validarWhatsappNoBlur() {
    const d = whatsapp.replace(/\D/g, '');
    if (d.length > 0 && !telefoneValido(whatsapp)) {
      setErrosComprador((e) => ({
        ...(e ?? {}),
        whatsapp: 'Informe seu WhatsApp para receber os ingressos.',
      }));
    }
  }

  async function pagar() {
    if (!mod) return;

    const campos: CampoGuiado[] = [];

    if (mod.exige_identificacao) {
      // Cada ingresso pede nome, CPF e WhatsApp de quem vai usar.
      const erros = cartoes.map((c, i) => {
        const e: { nome?: string; cpf?: string; whatsapp?: string } = {};
        if (!c.nome.trim()) e.nome = `Falta o nome de quem vai usar o ingresso ${i + 1}.`;
        const cpfLimpo = c.cpf.replace(/\D/g, '');
        if (!cpfLimpo) e.cpf = `Falta o CPF de quem vai usar o ingresso ${i + 1}.`;
        else if (!validarCPF(c.cpf)) e.cpf = 'CPF inválido — confira os 11 números.';
        if (!telefoneValido(c.whatsapp)) e.whatsapp = `Falta o WhatsApp do ingresso ${i + 1}.`;
        return e;
      });
      setErrosCartao(erros);
      erros.forEach((e, i) => {
        campos.push(
          {
            ref: document.getElementById(`ingresso-${i}-nome`),
            rotulo: `Ingresso ${i + 1} · nome`,
            mensagem: e.nome ?? null,
          },
          {
            ref: document.getElementById(`ingresso-${i}-cpf`),
            rotulo: `Ingresso ${i + 1} · CPF`,
            mensagem: e.cpf ?? null,
          },
          {
            ref: document.getElementById(`ingresso-${i}-whatsapp`),
            rotulo: `Ingresso ${i + 1} · WhatsApp`,
            mensagem: e.whatsapp ?? null,
          },
        );
      });
    } else if (!pularDados) {
      // Sem identificação: só nome e WhatsApp de quem participa.
      const erros: { nome?: string; whatsapp?: string } = {};
      if (!nomeComprador.trim()) erros.nome = 'Falta seu nome.';
      if (!telefoneValido(whatsapp))
        erros.whatsapp = 'Informe seu WhatsApp para receber os ingressos.';
      setErrosComprador(erros);
      campos.push(
        {
          ref: document.getElementById('ingresso-nome-comprador'),
          rotulo: 'Seu nome',
          mensagem: erros.nome ?? null,
        },
        {
          ref: document.getElementById('ingresso-whatsapp-comprador'),
          rotulo: 'Seu WhatsApp',
          mensagem: erros.whatsapp ?? null,
        },
      );
    }

    const invalidos = validar(campos);
    if (invalidos.length > 0) {
      irParaOPrimeiro(invalidos);
      return;
    }

    // CPF duplicado entre os ingressos.
    if (mod.exige_identificacao) {
      const vistos = new Set<string>();
      const duplicado = cartoes
        .map((c) => c.cpf.replace(/\D/g, ''))
        .find((cpf) => {
          if (vistos.has(cpf)) return true;
          vistos.add(cpf);
          return false;
        });
      if (duplicado) {
        const msg = `O CPF ${mascaraCPF(duplicado)} está repetido.`;
        setErro(msg);
        apontarErro(null, msg);
        return;
      }
    }

    setCriando(true);
    setErro(null);
    try {
      const carteiraSalva = await carteiraParaUsar(mod.evento_id);

      const pessoas = mod.exige_identificacao
        ? cartoes.map((c) => ({
            nome: c.nome.trim(),
            cpf: c.cpf.replace(/\D/g, ''),
            telefone: c.whatsapp.replace(/\D/g, ''),
          }))
        : Array.from({ length: quantidade }, () => ({}));

      // A carteira fica no nome e no WhatsApp do 1º ingresso (é por ele que se recupera).
      const pNome = mod.exige_identificacao
        ? cartoes[0].nome.trim()
        : pularDados
          ? null
          : nomeComprador.trim() || null;
      const pTelefone = mod.exige_identificacao
        ? cartoes[0].whatsapp.replace(/\D/g, '')
        : pularDados
          ? null
          : whatsapp.replace(/\D/g, '') || null;

      const { data: itens, error: err } = await supabase.rpc('comprar_ingressos_publico', {
        p_modalidade_id: Number(modalidadeId),
        p_pessoas: pessoas,
        p_opcao_id: opcaoIdNum,
        p_nome: pNome,
        p_telefone: pTelefone,
        p_email: null,
        p_carteira: carteiraSalva ?? null,
      });
      if (err) {
        const msg = mensagemAmigavel(err, 'Não foi possível concluir a compra. Tente novamente.');
        setErro(msg);
        // Tenta apontar o campo certo quando o nome aparece na mensagem do banco.
        const alvos = [
          ...cartoes.map((c, i) => ({ nome: c.nome.trim().toLowerCase(), id: `ingresso-${i}-nome` })),
          ...(!pularDados && !mod.exige_identificacao
            ? [{ nome: nomeComprador.trim().toLowerCase(), id: 'ingresso-nome-comprador' }]
            : []),
        ].filter((a) => a.nome.length > 0);
        const alvo = alvos.find((a) => msg.toLowerCase().includes(a.nome));
        apontarErro(alvo ? document.getElementById(alvo.id) : null, msg);
        return;
      }

      const lista = (itens as CompraIngresso[]) || [];
      if (lista.length === 0) {
        setErro('Não conseguimos concluir a compra. Tente novamente.');
        return;
      }

      const primeira = lista[0];
      if (primeira.carteira_codigo) {
        localStorage.setItem(chaveCarteiraLocal(mod.evento_id), primeira.carteira_codigo);
      }

      if ((primeira.total_centavos ?? 0) === 0) {
        navigate(`/carteira/${primeira.carteira_codigo}`, { replace: true });
      } else {
        navigate(`/pedido/c/${primeira.pedido_codigo}`, {
          replace: true,
          state: { eventoImagem: imagemCapa },
        });
      }
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos concluir a compra. Tente novamente.'));
    } finally {
      setCriando(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[560px] px-5 py-6">
          <div className="h-8 w-48 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-5 h-40 w-full animate-pulse rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-32 w-full animate-pulse rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-60 w-full animate-pulse rounded-[18px] bg-background-200/70" />
        </div>
      </div>
    );
  }

  if (erro && !mod) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível continuar
          </h1>
          <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
          >
            Ver eventos disponíveis
          </button>
        </Card>
      </div>
    );
  }

  if (encerrada && evento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-door-closed-line text-[30px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Vendas encerradas
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-foreground-600">{encerrada}</p>
          <button
            type="button"
            onClick={() => navigate(`/e/${evento.slug}`)}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
          >
            Ver evento
          </button>
        </Card>
      </div>
    );
  }

  if (bloqueio && evento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-500">
            <i className="ri-time-line text-[32px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Este lote ainda não está à venda
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-foreground-600">
            O lote anterior ainda está vendendo. Quando ele fechar ou esgotar, este abre
            automaticamente.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/e/${evento.slug}`)}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
          >
            Ver o evento
          </button>
        </Card>
      </div>
    );
  }

  const dataHora = evento ? formatarDataCurta(evento.inicio_em) : null;
  const local = evento
    ? [evento.local_nome, [evento.cidade, evento.uf].filter(Boolean).join(' - ')]
        .filter(Boolean)
        .join(', ')
    : null;
  const imagemCapa = evento?.imagem_mobile_url || evento?.imagem_desktop_url || null;
  const tipoIngresso = mod?.nome ? (opcaoNome ? `${mod.nome} · ${opcaoNome}` : mod.nome) : null;

  // O botão só habilita quando todos os cartões estão completos.
  const cartaoCompleto = (c: CartaoIngresso) =>
    !!c.nome.trim() && validarCPF(c.cpf) && telefoneValido(c.whatsapp);
  const podePagar = !mod
    ? false
    : mod.exige_identificacao
      ? cartoes.length > 0 && cartoes.every(cartaoCompleto)
      : pularDados
        ? true
        : !!nomeComprador.trim() && telefoneValido(whatsapp);

  // Toque no botão ainda cinza: mostra os erros e rola até o primeiro campo.
  function tentarPagar() {
    if (criando) return;
    setForcarErros(true);
    if (!podePagar) setTremor((t) => t + 1);
    void pagar();
  }

  return (
    <div className="min-h-screen bg-background-100 pb-40">
      <TopoIngresso
        nome={evento?.nome ?? 'Comprar ingresso'}
        imagem={imagemCapa}
        dataHora={dataHora}
        local={local}
        tipo={tipoIngresso}
      />

      <div className="app-entra mx-auto w-full max-w-[560px] px-4 py-4">
        <div className="space-y-3">
          <SeletorQuantidade
            quantidade={quantidade}
            max={maxPorCompra}
            onChange={mudarQuantidade}
          />

          <ResumoValores linhas={resumo} quantidade={quantidade} />

          {temCarteira && (
            <div className="rounded-[16px] border border-accent-200 bg-accent-50 px-4 py-3">
              <p className="flex items-center gap-2 text-[13.5px] font-semibold text-accent-900">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-user-heart-line" aria-hidden />
                </span>
                {nomeCarteira
                  ? `Comprando na carteira de ${nomeCarteira}`
                  : 'Comprando na sua carteira'}
              </p>
              <button
                type="button"
                onClick={esquecerCarteira}
                className="mt-0.5 cursor-pointer text-[12px] font-semibold text-accent-700 underline"
              >
                não é você?
              </button>
            </div>
          )}

          {!mod?.exige_identificacao && !pularDados && (
            <DadosComprador
              nome={nomeComprador}
              whatsapp={whatsapp}
              erros={errosComprador}
              tremer={tremor}
              onNome={mudarNomeComprador}
              onWhatsapp={mudarWhatsapp}
              onBlurWhatsapp={validarWhatsappNoBlur}
            />
          )}

          {mod?.exige_identificacao && (
            <div className="space-y-3">
              <div className="rounded-[16px] bg-background-50 p-4 shadow-pcard">
                <h2 className="text-[16px] font-extrabold text-foreground-950">
                  Quem vai usar cada ingresso
                </h2>
                <p className="mt-1 text-[13px] leading-snug text-foreground-600">
                  Nome, CPF e WhatsApp de cada pessoa. O ingresso chega no WhatsApp dela.
                </p>
              </div>

              {cartoes.map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="overflow-hidden pb-1"
                >
                  <CartaoIdentificacao
                    indice={i}
                    cartao={c}
                    mostrarSouEu={i === 0 && logado && !!perfilLogado}
                    erros={errosCartao[i] || {}}
                    forcarErros={forcarErros}
                    tremer={tremor}
                    onChange={(patch) => atualizarCartao(i, patch)}
                    onSouEu={preencherComigo}
                    onBlurNome={() => validarNomeNoBlur(i)}
                    onBlurCpf={() => validarCpfNoBlur(i)}
                    onBlurWhatsapp={() => validarWhatsappCartaoNoBlur(i)}
                  />
                </motion.div>
              ))}
            </div>
          )}

          {erro && (
            <div className="rounded-[14px] border border-danger-200 bg-danger-50 px-4 py-3 text-[13.5px] text-danger-700">
              {erro}
            </div>
          )}
        </div>
      </div>

      {/* Rodapé fixo: aviso do Pix + botão que só habilita com tudo completo */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur-md"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-full max-w-[560px]">
          <p className="mb-2 text-[12px] leading-snug text-foreground-500">
            Depois do Pix o ingresso chega no WhatsApp e fica em Meus ingressos.
          </p>
          <button
            type="button"
            onClick={tentarPagar}
            disabled={criando}
            aria-disabled={!podePagar || criando}
            className={`flex h-14 w-full items-center justify-center gap-2 whitespace-nowrap rounded-[16px] text-[16px] font-bold transition-colors ${
              podePagar && !criando
                ? 'aperta cursor-pointer bg-primary-500 text-white hover:bg-primary-600'
                : 'cursor-not-allowed bg-background-200 text-foreground-500'
            }`}
          >
            {criando ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" aria-hidden />
                Criando seu pedido…
              </span>
            ) : totalCentavos > 0 ? (
              <>Pagar {formatarCentavos(totalCentavos)} no Pix</>
            ) : (
              <>Garantir ingressos</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}